
import React, { useState, useEffect } from 'react';
import AddressAutocomplete from '@/components/ui-custom/AddressAutocomplete';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { ServiceType } from '@/context/LeadContext';
import { submitPublicLead } from '@/api/publicLead';
import { toast } from '@/components/ui/use-toast';
import ThankYouMessage from '@/components/ui-custom/ThankYouMessage';
import { validateName, validatePhone, validatePostalCode } from '@/utils/formValidation';
import { getAttribution } from '@/utils/attribution';
import { newEventId, trackLeadConversion } from '@/utils/fbTracking';
import { AlertCircle, Bolt, CheckCircle2, Clock3, Flame, Home, Leaf, MapPin, MessageSquare, Phone, ShieldCheck, Snowflake, Sun, User, Wind } from 'lucide-react';

interface GuestFormProps {
  onSubmitSuccess?: () => void;
}

const serviceOptions: Array<{
  value: ServiceType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  cardClass: string;
  iconClass: string;
}> = [
  {
    value: 'εξοικονομώ',
    label: 'Εξοικονομώ',
    icon: Home,
    cardClass: 'border-blue-200 bg-blue-50/70',
    iconClass: 'text-blue-600'
  },
  {
    value: 'αντλίεςΘερμότητας',
    label: 'Αντλίες Θερμότητας',
    icon: Wind,
    cardClass: 'border-cyan-200 bg-cyan-50/70',
    iconClass: 'text-cyan-600'
  },
  {
    value: 'φωτοβολταϊκά',
    label: 'Φωτοβολταϊκά',
    icon: Sun,
    cardClass: 'border-amber-200 bg-amber-50/70',
    iconClass: 'text-amber-600'
  },
  {
    value: 'κλιματιστικά',
    label: 'Κλιματιστικά',
    icon: Snowflake,
    cardClass: 'border-indigo-200 bg-indigo-50/70',
    iconClass: 'text-indigo-600'
  },
  {
    value: 'φυσικόΑέριο',
    label: 'Φυσικό Αέριο',
    icon: Flame,
    cardClass: 'border-rose-200 bg-rose-50/70',
    iconClass: 'text-rose-600'
  },
  {
    value: 'ηλιακά',
    label: 'Ηλιακά',
    icon: Leaf,
    cardClass: 'border-emerald-200 bg-emerald-50/70',
    iconClass: 'text-emerald-600'
  },
  {
    value: 'άλλο',
    label: 'Άλλο',
    icon: Bolt,
    cardClass: 'border-slate-200 bg-slate-50/70',
    iconClass: 'text-slate-600'
  },
];

export default function GuestForm({ onSubmitSuccess }: GuestFormProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [service, setService] = useState<ServiceType>('εξοικονομώ');
  const [timeframe, setTimeframe] = useState('');
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [errors, setErrors] = useState<{ name?: string; phone?: string; postalCode?: string; address?: string; timeframe?: string }>({});

  // Progress across the required fields — gives the visitor a reason to finish
  // on mobile, where the form is several screens long.
  const requiredFilled = [
    name.trim(),
    phone.trim(),
    address.trim(),
    postalCode.trim(),
    timeframe,
  ].filter(Boolean).length;
  const totalRequired = 5;
  const progressPct = Math.round((requiredFilled / totalRequired) * 100);

  // Validate a field once the user leaves it — never while they are still typing
  const handleBlur = (field: 'name' | 'phone' | 'postalCode') => {
    const check =
      field === 'name' ? validateName(name)
      : field === 'phone' ? validatePhone(phone)
      : validatePostalCode(postalCode);
    setErrors((prev) => ({ ...prev, [field]: check.valid ? undefined : check.error }));
  };

  useEffect(() => {
    if (showMessage) {
      const timer = setTimeout(() => {
        setShowMessage(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showMessage]);

  const handleLocationSelect = (lat: number, lng: number, postal: string) => {
    setLatitude(lat);
    setLongitude(lng);
    setPostalCode(postal);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Field-level validation — tells the customer exactly what to fix instead of
    // one generic "fill in the required fields" toast.
    const nameCheck = validateName(name);
    const phoneCheck = validatePhone(phone);
    const postalCheck = validatePostalCode(postalCode);
    const nextErrors = {
      name: nameCheck.valid ? undefined : nameCheck.error,
      phone: phoneCheck.valid ? undefined : phoneCheck.error,
      postalCode: postalCheck.valid ? undefined : postalCheck.error,
      address: address.trim() ? undefined : 'Συμπληρώστε τη διεύθυνσή σας.',
      timeframe: timeframe ? undefined : 'Επιλέξτε πότε θέλετε να ξεκινήσει.',
    };
    setErrors(nextErrors);

    const firstError = Object.entries(nextErrors).find(([, v]) => v);
    if (firstError) {
      toast({
        title: 'Ελέγξτε τα στοιχεία',
        description: firstError[1],
        variant: 'destructive',
      });
      document.getElementById(firstError[0])?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSubmitting(true);
    try {
      const attribution = getAttribution();
      const newLead = await submitPublicLead({
        name,
        phone,
        address,
        postalCode,
        service,
        timeframe,
        comments,
        latitude,
        longitude,
        source: attribution.source,
        campaign: attribution.campaign,
      });

      if (newLead) {
        setName('');
        setPhone('');
        setAddress('');
        setPostalCode('');
        setService('εξοικονομώ');
        setTimeframe('');
        setComments('');
        setLatitude(null);
        setLongitude(null);
        setErrors({});
        setShowMessage(true);

        // Report the conversion to Meta through both the Pixel and the
        // Conversions API, sharing one event id so it is counted once.
        // Deliberately not awaited — the visitor never waits on tracking.
        void trackLeadConversion(
          newLead.id,
          newEventId(),
          (import.meta as any).env?.VITE_API_BASE_URL || '/api',
          sessionStorage.getItem('csrf_token') || '',
          newLead.capiToken
        );

        // Call the onSubmitSuccess prop if it exists
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }

        toast({
          title: 'Επιτυχία',
          description: 'Η φόρμα υποβλήθηκε με επιτυχία!',
        });
      } else {
        toast({
          title: 'Σφάλμα',
          description: 'Υπήρξε ένα σφάλμα κατα την υποβολή, παρακαλώ προσπαθήστε ξανά.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: 'Σφάλμα',
        description: 'Υπήρξε ένα σφάλμα κατα την υποβολή, παρακαλώ προσπαθήστε ξανά.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-1 py-2 sm:px-2">
      {showMessage ? (
        <ThankYouMessage />
      ) : (
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-lg bg-emerald-100 p-2">
                <ShieldCheck className="h-4 w-4 text-emerald-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Δωρεάν εκτίμηση από ειδικό συνεργάτη</p>
                <p className="text-xs text-slate-600 mt-1">Η υποβολή διαρκεί λιγότερο από 1 λεπτό και επικοινωνούμε άμεσα.</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
            <p className="text-sm font-semibold text-slate-900 mb-3">1. Επίλεξε υπηρεσία</p>
            <div className="grid grid-cols-2 gap-3">
              {serviceOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = service === option.value;
                const isOtherOption = option.value === 'άλλο';

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setService(option.value)}
                    className={`group rounded-2xl border px-2 sm:px-3 text-center transition-all flex flex-col items-center justify-center relative ${
                      isOtherOption
                        ? 'col-span-2 min-h-[64px] sm:min-h-[84px] py-2 sm:py-2.5'
                        : 'min-h-[96px] sm:min-h-[124px] py-3 sm:py-4'
                    } ${
                      isSelected
                        ? `${option.cardClass} ring-2 ring-offset-0 ring-primary/35 border-primary/40 shadow-sm`
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {isSelected && <CheckCircle2 className="h-4 w-4 text-emerald-600 absolute right-2.5 top-2.5" />}
                    <div className={`rounded-xl p-2 sm:p-2.5 ${isSelected ? 'bg-white/85' : 'bg-slate-100'}`}>
                      <Icon className={`h-6 w-6 sm:h-7 sm:w-7 ${isSelected ? option.iconClass : 'text-slate-500'}`} />
                    </div>
                    <div className="text-sm sm:text-base font-semibold text-slate-900 leading-tight mt-1.5 sm:mt-2">
                      {option.label}
                    </div>
                  </button>
                );
              })}
            </div>
            {service === 'άλλο' && (
              <p className="text-xs text-slate-500 mt-2">
                Περιγράψτε τι χρειάζεστε στα «Σχόλια» πιο κάτω.
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-5">
            <p className="text-sm font-semibold text-slate-900">2. Συμπλήρωσε στοιχεία επικοινωνίας</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-500" />
                  Όνομα *
                </label>
                <Input
                  type="text"
                  id="name"
                  autoComplete="name"
                  placeholder="Π.χ. Γιάννης Παπαδόπουλος"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => handleBlur('name')}
                  aria-invalid={!!errors.name}
                  className={`h-12 bg-slate-50 rounded-xl focus-visible:ring-primary/30 ${errors.name ? 'border-red-400 focus-visible:ring-red-300' : 'border-slate-200'}`}
                  required
                />
                {errors.name && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {errors.name}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="phone" className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-500" />
                  Τηλέφωνο *
                </label>
                <Input
                  type="tel"
                  id="phone"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="Π.χ. 6912345678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onBlur={() => handleBlur('phone')}
                  aria-invalid={!!errors.phone}
                  className={`h-12 bg-slate-50 rounded-xl focus-visible:ring-primary/30 ${errors.phone ? 'border-red-400 focus-visible:ring-red-300' : 'border-slate-200'}`}
                  required
                />
                {errors.phone ? (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {errors.phone}
                  </p>
                ) : (
                  <p className="text-xs text-slate-500">Σε αυτό το τηλέφωνο θα σας καλέσει ο συνεργάτης.</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="address" className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-slate-500" />
                Διεύθυνση *
              </label>
              <AddressAutocomplete
                value={address}
                onChange={setAddress}
                onLocationSelect={handleLocationSelect}
                placeholder="Πληκτρολογήστε διεύθυνση"
              />
              <p className="text-xs text-slate-500">Η σωστή τοποθεσία βοηθά στην ταχύτερη ανάθεση συνεργάτη.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="postalCode" className="text-sm font-semibold text-slate-800">ΤΚ *</label>
                <Input
                  type="text"
                  id="postalCode"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  maxLength={5}
                  placeholder="Π.χ. 11527"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  onBlur={() => handleBlur('postalCode')}
                  aria-invalid={!!errors.postalCode}
                  className={`h-12 bg-slate-50 rounded-xl focus-visible:ring-primary/30 ${errors.postalCode ? 'border-red-400 focus-visible:ring-red-300' : 'border-slate-200'}`}
                  required
                />
                {errors.postalCode && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {errors.postalCode}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="timeframe" className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Clock3 className="h-4 w-4 text-slate-500" />
                  Χρονικό πλαίσιο *
                </label>
                <Select
                  value={timeframe}
                  onValueChange={(v) => { setTimeframe(v); setErrors((prev) => ({ ...prev, timeframe: undefined })); }}
                >
                  <SelectTrigger
                    id="timeframe"
                    className={`h-12 bg-slate-50 rounded-xl focus:ring-primary/30 ${errors.timeframe ? 'border-red-400' : 'border-slate-200'}`}
                  >
                    <SelectValue placeholder="Πότε θέλετε να ξεκινήσει;" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="άμεσα">Άμεσα</SelectItem>
                    <SelectItem value="εντός3μηνών">Εντός 3 μηνών</SelectItem>
                    <SelectItem value="κάνωΈρευνα">Κάνω έρευνα αγοράς</SelectItem>
                  </SelectContent>
                </Select>
                {errors.timeframe && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {errors.timeframe}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="comments" className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-slate-500" />
                Σχόλια (προαιρετικά)
              </label>
              <Textarea
                id="comments"
                placeholder="Γράψτε λεπτομέρειες που θα βοηθήσουν την προσφορά"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl min-h-[110px] focus-visible:ring-primary/30"
              />
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600 flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600" />
            Τα στοιχεία χρησιμοποιούνται αποκλειστικά για την επικοινωνία σχετικά με το αίτημά σας.
          </div>

          <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold">
            {isSubmitting ? 'Υποβολή...' : 'Λήψη δωρεάν προσφοράς'}
          </Button>

          {/* Spacer taller than the sticky bar (~106px) so nothing hides behind it */}
          <div className="h-32 sm:hidden" aria-hidden="true" />

          {/* Sticky mobile CTA — the form runs several screens on a phone, so the
              action stays reachable and progress stays visible while scrolling. */}
          <div className="sm:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur px-4 pt-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] shadow-[0_-4px_16px_rgba(15,23,42,0.08)]">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-medium text-slate-600">
                {requiredFilled === totalRequired
                  ? 'Όλα έτοιμα — στείλτε το αίτημα'
                  : `Συμπληρώθηκαν ${requiredFilled} από ${totalRequired}`}
              </span>
              <span className="text-[11px] font-semibold text-slate-500">{progressPct}%</span>
            </div>
            <div className="h-1 w-full rounded-full bg-slate-200 overflow-hidden mb-2.5">
              <div
                className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold">
              {isSubmitting ? 'Υποβολή...' : 'Λήψη δωρεάν προσφοράς'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
